/**
 * Shared types and helpers for the catalog screens.
 *
 * Every money field arrives from the API as a Decimal *string* — it is never
 * parsed to a float for display arithmetic (§ money rules); `formatCurrency`
 * takes the string straight through.
 */
import { CatalogItemType, EntitlementType, ResourceType } from '@/lib/shared-types';
import { isPreparable } from '@/lib/domain';
import { api } from '@/lib/api';

/**
 * X-12 — `CatalogItemType`, `EntitlementType` and `ResourceType` are Prisma enums
 * carried by `@/lib/shared-types`. Re-exported so the catalog tabs keep one
 * import; there is no local copy to fall out of step with the schema.
 */
export { CatalogItemType, EntitlementType, ResourceType };

export interface Translation {
  locale: string;
  name: string;
  description?: string | null;
}

export interface CategoryRef {
  id: string;
  name: string;
}

export interface CatalogItem {
  id: string;
  name: string;
  type: CatalogItemType;
  categoryId: string | null;
  stationId: string | null;
  sku: string | null;
  barcode: string | null;
  description: string | null;
  imageUrl: string | null;
  basePrice: string;
  taxRate: string;
  sortOrder: number;
  isSoldOut: boolean;
  isActive: boolean;
  category?: CategoryRef | null;
  station?: { id: string; name: string; code: string } | null;
  translations?: Translation[];
  variants?: Variant[];
  modifierGroups?: AttachedModifierGroup[];
  /**
   * "Main + side + drink" on a `MEAL_COMBO`, from the single-item read.
   *
   * Declared for the one question an ordering screen has to ask before it rings
   * a meal up: `order-pricing.ts` answers 400 to a meal whose required group is
   * unfilled, so a till that cannot offer the choices must know they exist
   * rather than compose a line that is going to be refused.
   */
  componentGroups?: MealComponentGroup[];
  _count?: { variants: number; modifierGroups: number };
}

/** One choose-one-of block inside a meal. Its options are other catalog items. */
export interface MealComponentGroup {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  sortOrder: number;
}

export interface Variant {
  id: string;
  name: string;
  sku: string | null;
  priceDelta: string;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface AttachedModifierGroup {
  groupId: string;
  sortOrder: number;
  group: ModifierGroup;
}

export interface Modifier {
  id: string;
  name: string;
  price: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ModifierGroup {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  sortOrder: number;
  modifiers?: Modifier[];
  _count?: { items: number };
}

export interface Category {
  id: string;
  name: string;
  parentId: string | null;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  translations?: Translation[];
  children?: Category[];
  _count?: { children: number; items: number };
}

export interface ScheduleWindow {
  days: number[];
  from: string;
  to: string;
}

export interface MenuItemRow {
  menuId: string;
  itemId: string;
  sortOrder: number;
  isVisible: boolean;
  item: {
    id: string;
    name: string;
    type: string;
    basePrice: string;
    isActive: boolean;
    isSoldOut: boolean;
    categoryId: string | null;
  };
}

export interface Menu {
  id: string;
  name: string;
  branchId: string | null;
  schedule: ScheduleWindow[] | null;
  isDefault: boolean;
  isActive: boolean;
  branch?: { id: string; name: string } | null;
  items?: MenuItemRow[];
}

export interface MealComponentOption {
  id: string;
  itemId: string;
  extraCharge: string;
  isDefault: boolean;
  quantity: number;
  item?: { id: string; name: string; basePrice?: string; isSoldOut?: boolean };
}

export interface MealComponentGroup {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  sortOrder: number;
  options?: MealComponentOption[];
}

export interface Meal extends CatalogItem {
  componentGroups?: MealComponentGroup[];
}

export interface MealPrice {
  itemId: string;
  name: string;
  basePrice: string;
  variantDelta: string;
  extras: string;
  total: string;
  taxRate: string;
  breakdown: Array<{
    groupId: string;
    groupName: string;
    optionId: string;
    itemId: string;
    name: string;
    quantity: number;
    extraCharge: string;
    lineTotal: string;
  }>;
}

export interface EntitlementTemplate {
  id: string;
  type: EntitlementType;
  catalogItemId: string | null;
  quantity: string | null;
  resourceId: string | null;
  resourceType: string | null;
  minutes: number | null;
  catalogItem?: { id: string; name: string } | null;
  resource?: { id: string; name: string; type: string } | null;
}

export interface Package {
  id: string;
  name: string;
  description: string | null;
  price: string;
  validityMinutes: number | null;
  catalogItemId: string | null;
  isActive: boolean;
  entitlements?: EntitlementTemplate[];
  catalogItem?: { id: string; name: string } | null;
}

export interface Station {
  id: string;
  name: string;
  code: string;
  slaSeconds?: number;
}

export const CATALOG_ITEM_TYPES: CatalogItemType[] = Object.values(CatalogItemType);

export const RESOURCE_TYPES: ResourceType[] = Object.values(ResourceType);

/** Surface the API's own message rather than a generic "something went wrong". */
export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(' · ');
  return message ?? fallback;
}

/**
 * Whether a refused request was the global throttle rather than the server's
 * final answer on the merits (`ThrottlerException`, 429 — see
 * `RETRYABLE_4XX` in `lib/offline/errors.ts` for the same call made for the
 * offline queue). Worth a different sentence: a 429 is worth retrying, and
 * retrying it immediately just spends the same budget again.
 */
export function isRateLimited(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 429;
}

/**
 * A window whose `to` is strictly before its `from` runs past midnight.
 *
 * Equality is deliberately excluded: `MenusService.isScheduleOpen` reads a
 * window as `from <= to ? now >= from && now < to : now >= from || now < to`,
 * so `to === from` takes the first branch and can never be satisfied. Calling
 * that "crosses midnight" would tell the operator their 08:00–08:00 window runs
 * all night when the server treats it as permanently shut — see `neverOpens`.
 */
export function crossesMidnight(window: ScheduleWindow): boolean {
  return window.to < window.from;
}

/** A window the server can never satisfy, because `from === to` is an empty
 *  half-open interval. Accepted by the API, so it is a warning, not a block. */
export function neverOpens(window: ScheduleWindow): boolean {
  return !!window.from && window.to === window.from;
}

/** `MenuScheduleWindowDto` validates both times with `/^([01]\d|2[0-3]):[0-5]\d$/`,
 *  and an emptied `<input type="time">` reports `''`. */
export function isValidWindowTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/**
 * The `step` an `<input type="number">` needs to express one minor unit of a
 * currency with `decimals` of them. `0.01` is wrong in Kuwait, where it makes a
 * fil unreachable, and wrong in Japan, where there is nothing below a yen.
 */
export function stepFor(decimals: number): string {
  return decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`;
}

/**
 * The step for a money field whose DTO validates `@IsNumber({ maxDecimalPlaces: 2 })`
 * — catalog `basePrice`, variant `priceDelta`, modifier `price`, meal
 * `extraCharge` and price-list `price` all do.
 *
 * A three-minor-unit currency (KWD, BHD, OMR) would otherwise get a `0.001`
 * step on a field the API rejects at that precision, so the input would be
 * inviting a guaranteed 400. Cap the offer at what the endpoint accepts.
 */
export function apiMoneyStep(decimals: number): string {
  return stepFor(Math.min(decimals, 2));
}

/**
 * The next barcode in this tenant's own run — 000001, 000002, and so on.
 *
 * Server-side because only the server knows what is already taken; a client
 * that guessed would collide the moment two people added a product at once.
 */
export async function nextBarcode(): Promise<string> {
  const { data } = await api.get<{ data?: { barcode: string } } | { barcode: string }>(
    '/catalog/next-barcode',
  );
  return (data as { data?: { barcode: string } }).data?.barcode ?? (data as { barcode: string }).barcode;
}

/**
 * Types that a station can prepare.
 *
 * The list itself moved to `@/lib/domain`: the item form was the only
 * thing that obeyed it, so the bulk edit, the import and the kitchen's own
 * "always route this product" action could each put a station on an hour of
 * PS5 and nothing refused. The API enforces it from the same constant now.
 */
export { isPreparable };
